import React, { useRef, useState, useEffect, useMemo } from 'react'
import { func, shape, string, bool, arrayOf } from 'prop-types'
import Popper from '@mui/material/Popper'
import Grow from '@mui/material/Grow'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import { KeyboardArrowDown } from '@mui/icons-material'
import Typography from '@mui/material/Typography'
import { withStyles } from '~/themes/useStyles'
import NumberInput from '../NumberInput'
import style from './style'

export const DEFAULT_UNIT_TYPE = {
  PERCENTAGE: 'percentage',
  DOLLAR_VALUE: 'fixed_amount',
}

export const DEFAULT_VALUE = {
  amount: 0,
  unit: DEFAULT_UNIT_TYPE.DOLLAR_VALUE,
}

const DEFAULT_OPTIONS = [
  {
    label: 'Dollar Value',
    value: DEFAULT_UNIT_TYPE.DOLLAR_VALUE,
    unit: '$',
  },
  {
    label: 'Percentage',
    value: DEFAULT_UNIT_TYPE.PERCENTAGE,
    unit: '%',
  },
]

const DiscountInput = ({
  classes,
  onSelect,
  type,
  disabled,
  helperText,
  options,
  fullWidth,
  ...props
}) => {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)

  const selectedOption = useMemo(
    () => (options || []).find((o) => o.value === type),
    [type, options],
  )

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus()
    }

    prevOpen.current = false
  }, [open])

  const handleListKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    }
  }

  const handleSelect = (val) => {
    onSelect(val)
    setOpen(false)
  }

  const renderAdorment = () => (
    <Box display="flex" alignItems="center" pr={disabled ? 1 : 0}>
      <Typography className={classes.unit}>{selectedOption?.unit}</Typography>
      {disabled || (
        <IconButton size="small" onClick={handleToggle}>
          <KeyboardArrowDown color="primary" />
        </IconButton>
      )}
    </Box>
  )

  return (
    <Box className={classes.discountInput}>
      <NumberInput
        inputRef={anchorRef}
        disableScrollAction
        containerClassName={classes.numberInputNoArrow}
        helperText={helperText}
        InputProps={{
          endAdornment: renderAdorment(),
        }}
        disabled={disabled}
        {...props}
      />
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        className={classes.poper}
        placement="bottom-start"
        style={{
          width: fullWidth
            ? `${(anchorRef?.current?.offsetWidth || 0) + 52}px`
            : 'auto',
        }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {options.map((item) => (
                    <MenuItem
                      key={item.value}
                      style={{ fontSize: 14 }}
                      onClick={() => handleSelect(item.value)}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  )
}

DiscountInput.defaultProps = {
  type: '',
  helperText: '',
  disabled: false,
  options: DEFAULT_OPTIONS,
  fullWidth: false,
}

DiscountInput.propTypes = {
  classes: shape().isRequired,
  onSelect: func.isRequired,
  disabled: bool,
  type: string,
  helperText: string,
  fullWidth: bool,
  options: arrayOf(shape()),
}

export default withStyles(style)(DiscountInput)
